import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config/api';
import {
  hasAnyPermission as checkAnyPermission,
  hasPermission as checkPermission,
  hasRole as checkRole,
  isSystemAdmin as checkIsSystemAdmin,
} from '../utils/permissions';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

const clearLocalSession = () => {
  localStorage.removeItem('token');
  try {
    sessionStorage.removeItem('admin_unlock_token');
    localStorage.removeItem('admin_unlock_token');
    sessionStorage.removeItem('sqmt_maintenance_notice_dismissed');
  } catch {
    /* ignore */
  }
};

const describeFetchFailure = (url, error, timedOut, timeoutMs) => {
  const target = url || API_BASE_URL || '(URL no configurada)';
  const browserHint = error?.message && error.message !== 'The user aborted a request.'
    ? ` Detalle: ${error.message}.`
    : '';

  if (timedOut) {
    return (
      `Tiempo de espera agotado (${Math.round(timeoutMs / 1000)}s) al contactar ${target}.` +
      ` El backend no respondió a tiempo (puede estar caído, sobrecargado o reiniciando).`
    );
  }

  return (
    `No se pudo conectar con ${target}.${browserHint}` +
    ` Revisa red, CORS o que el backend esté activo.`
  );
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    throw new Error(describeFetchFailure(url, error, timedOut || error?.name === 'AbortError', timeoutMs));
  } finally {
    clearTimeout(timeoutId);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    clearLocalSession();
    setToken(null);
    setUser(null);
  }, []);

  /** Cierre de sesión intencional: notifica al backend (audit + presencia). */
  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
          },
        });
      } catch {
        /* best-effort: igual limpiamos local */
      }
    }
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    fetchWithTimeout(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Token inválido');
        const text = await res.text();
        if (!text) throw new Error('Token inválido');
        try {
          return JSON.parse(text);
        } catch {
          throw new Error('Token inválido');
        }
      })
      .then((data) => {
        setUser(data);
        setLoading(false);
      })
      .catch(() => {
        // Token inválido/expirado: no registrar logout intencional
        clearSession();
        setLoading(false);
      });
  }, [token, clearSession]);

  const login = async (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await fetchWithTimeout(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail || error.error;
      if (response.status === 429) {
        throw new Error(
          detail || 'Demasiados intentos de login. Espera un minuto e inténtalo de nuevo.'
        );
      }
      if (response.status === 401 || response.status === 403) {
        throw new Error(detail || 'Credenciales inválidas');
      }
      if (response.status >= 500) {
        throw new Error(
          detail ||
            `Error del servidor (${response.status}) en ${API_BASE_URL}/api/auth/login. Revisa logs del backend.`
        );
      }
      throw new Error(detail || `Error de autenticación (HTTP ${response.status})`);
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const hasPermission = useCallback((code) => checkPermission(user, code), [user]);
  const hasAnyPermission = useCallback((codes) => checkAnyPermission(user, codes), [user]);
  const hasRole = useCallback((code) => checkRole(user, code), [user]);
  const isSystemAdmin = useCallback(() => checkIsSystemAdmin(user), [user]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    getAuthHeaders,
    isAuthenticated: !!user,
    hasPermission,
    hasAnyPermission,
    hasRole,
    isSystemAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
